(ns hypnos.common.subscription
  (:require ["aws-amplify" :as amp :refer [API]]
            [clojure.walk :as walk :refer [keywordize-keys]]
            [hypnos.graphql.queries :as queries]
            [re-frame.db :as rfdb :refer [app-db]]))

(def ^:private pollers
  (atom {}))

(defn- stringify
  "Coerces <x> to a string via clojure.core/name, or clojure.core/str if <x>
   does not implement the clojure.lang.Named interface."
  [x]
  (try (name x) (catch js/Error _ (str x))))

(defn poll-for-events!
  "This function starts a background process (via repeated calls to setTimeout)
   that polls the events graphql endpoint periodically and executes an event
   handler whenver a new event is received. The <id> is an arbitrary value used
   to ensure that at most one poller associated with that <id> is active at any
   time. The <topic> and <subtopic> identify the event to poll. The <interval>
   determines how long to wait between polling requests to the graphql endpoint.
   The <subscribed?>, <init>, and <change> handlers are described below. When
   an error is raised by the graphql request the <error> handler is called with
   the error object passed as its single argument.

   Lifecycle:

      1. Initialize state to ::nil (a no-value sentinel).
      2. Check if we should poll by executing the <subscribed?> handler,
         passing the re-frame app-db value as its single argument. If the
         result is false or nil the process ends.
      3. Fetch latest sequence id from the graphql events endpoint.
      4. Upon obtaining the latest sequence id the <init> handler is exe-
         cuted and the state is reset to the latest sequence id.
      5. Schedule the next iteration via setTimeout(). After the timeout
         continue from step (6).
      6. Check if polling should continue by executing the <subscribed?>
         handler as in (2). The process ends if the result is false or nil.
      7. Fetch the next sequence id from the graphql events endpoint.
      8. If the stored sequence id is not equal to the fetched sequence id
         the <change> handler is executed with no arguments and the state
         is updated with the new sequence id.
      9. GOTO step (5).

   Example:

      (subscription/poll-for-events!
        :id           :onRecommendMasks
        :topic        :onRecommendMasks
        :subtopic     patient-id
        :interval     5000
        :dedup-key    #(vector :onRecommendMasks patient-id panel-id)
        :subscribed?  #(-> (:active-panel (:ui %))
                          ((juxt :name (comp :patient-id :params)))
                          (= [:patient-panel patient-id]))
        :init         #(rf/dispatch [:amplify/query
                                    queries/get-patient-by-id
                                    {:id patient-id}
                                    :dme/set-patient
                                    :dme/error])
        :change       #(.reload js/location)
        :error        #(js/console.error %))
   "
  [& {:keys [id topic subtopic interval dedup-key subscribed? init change error]
      :or   {interval 10000 dedup-key #() subscribed? #() init #() change #() error #()}}]
  (let [state     (atom ::nil)
        query     queries/poll-event
        topic     (stringify topic)
        subtopic  (stringify subtopic)
        variables {:topic topic :subtopic subtopic}
        subbed?   #(subscribed? @app-db)
        locked?   #(-> @pollers id (= (dedup-key)))]
    (when (and (subbed?) (not (locked?)))
      (letfn [(fetch-data []
                (-> API
                    (.graphql (clj->js {:query query :variables variables}))
                    (.then    #(->> % js->clj keywordize-keys :data (reset! state)))
                    (.catch   error)
                    (.finally #(js/setTimeout poll-next interval))))
              (poll-next []
                (cond (not (locked?)) ::done-lost-lock
                      (not (subbed?)) (swap! pollers dissoc id)
                      :else           (fetch-data)))]
        (add-watch state 0 #(cond (= ::nil %3) (init) (not= %3 %4) (change)))
        (swap! pollers assoc id (dedup-key))
        (poll-next)))))
